import type { BaseQueryFn } from '@reduxjs/toolkit/query';
import type { AxiosRequestConfig } from 'axios';

import { isAxiosError } from 'axios';

import type { ServiceRequestConfig } from '~/services/createService';

import { k2 } from '~/services/serviceInstances';

export type K2BaseQueryArg = {
  url: string;
  method?: AxiosRequestConfig['method'];
  body?: unknown;
  params?: Record<string, unknown>;
  rtkQueryOptions?: ServiceRequestConfig;
};

const normalizeUrl = (rawUrl: string) => {
  const withoutApiPrefix = rawUrl.replace(/^\/?api\/v1/, '');
  return withoutApiPrefix.startsWith('/')
    ? withoutApiPrefix
    : `/${withoutApiPrefix}`;
};

export const k2BaseQuery: BaseQueryFn<
  K2BaseQueryArg,
  unknown,
  unknown
> = async ({ url, method = 'get', body, params, rtkQueryOptions }, api) => {
  const path = normalizeUrl(url);
  const config: ServiceRequestConfig = {
    showErrorToast: true,
    ...rtkQueryOptions,
    params,
    paramsSerializer: { indexes: null },
    /** Without this, `promise.abort()` rejects the thunk but leaves the request in flight. */
    signal: api.signal,
  };

  try {
    let result: unknown;
    const m = String(method).toLowerCase();
    if (m === 'get') {
      result = await k2.get(path, config);
    } else if (m === 'post') {
      result = await k2.post(path, body, config);
    } else if (m === 'patch') {
      result = await k2.patch(path, body, config);
    } else if (m === 'put') {
      result = await k2.put(path, body, config);
    } else if (m === 'delete') {
      result = await k2.remove(path, config);
    } else {
      return {
        error: {
          status: 400,
          data: `Unsupported method: ${method}`,
        },
      };
    }
    return { data: result };
  } catch (e) {
    if (isAxiosError(e)) {
      return {
        error: {
          status: e.response?.status ?? 'FETCH_ERROR',
          data: e.response?.data ?? e.message,
        },
      };
    }
    return { error: { status: 'FETCH_ERROR', error: String(e) } };
  }
};
